/* eslint-disable react/jsx-no-bind */
/* eslint-disable id-length */
import { Node, NodeProps } from "@xyflow/react";
import {
  CommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
} from "app/lib/pipeline";
import BaseNode from "../BaseNode";
import Commands from "./Commands";
import Plugins from "./Plugins";
import ArtifactPaths from "./ArtifactPaths";
import { getDockerPlugin } from "app/lib/pipeline/plugins";
import * as HoverCard from "../StepHoverCard";
import {
  useBuild,
  useJobsForStep,
  useMostRecentMatrixJobsForStep,
  useMostRecentParallelJobsForStep,
} from "app/components/build/Show/lib/BuildContext";
import { StateIcon, StateLabel } from "../BaseNode/StateIcon";
import { bgColorForStep, textColorForStep } from "../colors";
import { Jobs } from "../Jobs";
import { twMerge } from "tailwind-merge";
import { CommandJob } from "app/components/build/Show/lib/types";
import { JobDuration } from "app/components/job/Job/JobDuration";
import { MissingStepDependencies } from "../MissingStepDependencies";
import { Link } from "react-router-dom";
import CommandStepLabel from "app/components/build/Show/components/CommandStepLabel";

const JobCount = ({ jobs, step }: { jobs: CommandJob[]; step: CommandStep }) => {
  const passCount = jobs.filter((job) => job.passed).length;
  const totalCount = jobs.length;

  return (
    <span
      className={twMerge(
        "rounded-[4px] text-charcoal-700 px-1 border border-current",
        bgColorForStep(step),
        "bg-white",
      )}
    >
      {passCount}/{totalCount}
    </span>
  );
};

type CommandStepNodeType = Node<CommandStep, "command">;

export default function CommandStepNode(props: NodeProps<CommandStepNodeType>) {
  const { build } = useBuild({ strict: false });

  let jobs: CommandJob[] = [];
  if (isParallelCommandStep(props.data)) {
    jobs = useMostRecentParallelJobsForStep(props.id);
  } else if (isMatrixCommandStep(props.data)) {
    jobs = useMostRecentMatrixJobsForStep(props.id);
  } else {
    jobs = useJobsForStep(props.id);
  }

  const latestJob = jobs.length ? jobs[jobs.length - 1] : null;

  const { plugins, artifactPaths } = props.data;
  const docker = plugins ? getDockerPlugin(plugins) : null;
  const { missingDependencies = [] } = props.data;
  const waitingOnConcurrencyGroup = latestJob?.state === "limited";

  const label = <CommandStepLabel {...props.data} />;

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {build && latestJob && props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={props.data.missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            <div className="space-y-3">
              <div>
                <div className="flex gap-2 items-baseline">
                  <div className="flex-auto min-w-0 font-medium">{label}</div>
                  <div className="flex items-center gap-2 ml-auto">
                    <StateLabel {...props.data} />
                    <StateIcon
                      className={textColorForStep({
                        ...props.data,
                        waitingOnConcurrencyGroup,
                      })}
                      {...props.data}
                      waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
                    />
                  </div>
                </div>

                {isDefaultCommandStep(props.data) && (
                  <JobDuration job={latestJob} build={build} className="text-charcoal-300" />
                )}
              </div>

              <MissingStepDependencies missingDependencies={missingDependencies} build={build} />

              <Commands data={props.data} dockerImage={docker?.image} />

              {(isParallelCommandStep(props.data) || isMatrixCommandStep(props.data)) && (
                <div
                  className={twMerge(
                    bgColorForStep(props.data),
                    "border-t border-opacity-50 -mx-3 p-3 pb-0 space-y-3 bg-white",
                  )}
                >
                  <Jobs jobs={jobs} />
                </div>
              )}

              {!Features.BuildSidebar && isDefaultCommandStep(props.data) && (
                <div>
                  <Link
                    to={latestJob.path}
                    onClick={(e) => e.stopPropagation()}
                    className="btn-depth btn-small no-underline p-2 hover:no-underline"
                  >
                    Go to job
                  </Link>
                </div>
              )}
            </div>
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div>
          <BaseNode
            {...props}
            label={label}
            count={
              (isParallelCommandStep(props.data) || isMatrixCommandStep(props.data)) && (
                <JobCount jobs={jobs} step={props.data} />
              )
            }
          >
            {!props.data.state && <Commands data={props.data} dockerImage={docker?.image} />}
            {plugins && <Plugins plugins={plugins} />}
            {artifactPaths && <ArtifactPaths paths={artifactPaths} />}
          </BaseNode>
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}
